
// Base form that all asset types inherit from
export interface AssetForm {
  name: string;
  description?: string;
  assetTypeId: number;
  tag?: string;
  parentId?: number;
}

// Asset Type interface (from API)
export interface AssetType {
  id: number;
  name: string;
  assetSystemType
}

// Building form - matches building FORM_CONFIG
export interface BuildingForm extends AssetForm {
  // Address fields
  streetAddress: string;
  place: string;
  zipCode: string;
  country: string;

  // Basic building info
  totalArea?: number;
  buildYear?: number;
  renovatedYear?: number; // Added missing field


  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

// Solar installation form - matches solar FORM_CONFIG
export interface SolarForm extends AssetForm {
  installedCapacity: number; // kW - required
}

// Solar inverter form - matches solar_inverter FORM_CONFIG
export interface SolarInverterForm extends AssetForm {
  installedCapacity: number; // kW - required (labeled as "Capacity" in config)
}

// Solar string form - matches solar_string FORM_CONFIG
export interface SolarStringForm extends AssetForm {
  installedCapacity: number; // kW - required
  numberOfPanels: number; // required
}

// Street light form - matches street_light FORM_CONFIG
export interface StreetLightForm extends AssetForm {
  voltage: number; // required - V
  manufacturerType?: string; // "Manufacturer/Type"
  lightSource: 'LED' | 'HALOGEN' | 'SODIUM' | 'FLUORESCENT'; // required select
  power: number; // required - W
  dimmable?: 'true' | 'false' | boolean; // select with Yes/No options (handle both string and boolean)
}

// Street light junction box form - matches street_light_junction_box FORM_CONFIG
export interface StreetLightJunctionBoxForm extends AssetForm {
  totalTerminals: number; // required
  voltage: number; // required - V
  ignition?: string; // "Ignition Type"
}

// Ventilation form - matches ventilation FORM_CONFIG
export interface VentilationForm extends AssetForm {
  placement: string; // required - "Location/Placement"
  manufacturer: string; // required
  model: string; // required
  ratedCapacity: number; // required - m³/h
  ventilationZone?: string; // optional
  heating?: string; // optional - "Heating System"
  cooling?: string; // optional - "Cooling System"
  heatExchangerType?: '0' | '1' | '2' | '3'; // select: None, Plate, Rotary, Cross-flow
  isDemandControlled?: 'true' | 'false' | boolean; // select: Yes/No (handle both string and boolean)
  isPressureControlled?: 'true' | 'false' | boolean; // select: Yes/No (handle both string and boolean)
  driverTypes?: '1' | '2' | '3'; // select: Variable Speed Drive, Fixed Speed, Multi-Speed
}

// Heat pump form - matches heat_pump FORM_CONFIG
export interface HeatPumpForm extends AssetForm {
  manufacturer: string; // required
  model: string; // required
}

// Boiler form - matches boiler FORM_CONFIG
export interface BoilerForm extends AssetForm {
  manufacturer: string; // required
  model: string; // required
}

// Distribution board form - matches distribution_board FORM_CONFIG
export interface DistributionBoardForm extends AssetForm {
  placement: string; // required - "Location/Placement"
}

// Building automation form - matches building_automation FORM_CONFIG
export interface BuildingAutomationForm extends AssetForm {
  manufacturer: string; // required
  driverTypes?: '1' | '2' | '3' | '4'; // select: BACnet, Modbus, KNX, LON
}

// Heat exchanger form - matches heat_exchanger FORM_CONFIG
export interface HeatExchangerForm extends AssetForm {
  placement: string; // required - "Location"
  heatExchangerInlet?: number; // optional - °C
  heatExchangerOutlet?: number; // optional - °C
  heatExchangerType: '0' | '1' | '2' | '3'; // required select: Plate, Shell and Tube, Coil, Regenerative
  inletTemperature?: number; // optional - °C
  outletTemperature?: number; // optional - °C
  ratedPower: number; // required - kW
}



// Union type for all forms
export type AssetFormUnion =
  | BuildingForm
  | SolarForm
  | SolarInverterForm
  | SolarStringForm
  | StreetLightForm
  | StreetLightJunctionBoxForm
  | VentilationForm
  | HeatPumpForm
  | BoilerForm
  | DistributionBoardForm
  | BuildingAutomationForm
  | HeatExchangerForm;

// Full API Asset interface (what the API returns)
export interface ApiAsset {
  id: number;
  parentId: number;
  name: string;
  tag?: string;
  isActive: boolean;
  assetType: {
    id: number;
    name: string;
    icon: string;
  };
  position?: {
    type: string;
    coordinates: number[];
  };
  // Add other properties as needed
  [key: string]: any; // Flexible for other properties
}

// Full API request format (what we send to the API)
export interface ApiCreateAssetRequest {
  parentId?: number;
  assetAPIConfiguration?: {
    externalId: string;
    apiConfigurationId: number;
  };
  name: string;
  description?: string;
  assetTypeId: number;
  tag?: string;
  heatingDegree?: number;
  temperatureDependentConsumption?: number;
  energyReferenceYear?: number;
  inheritFromType?: boolean;
  assetAvailability?: number;
  fileReferences?: string[];
  categoryItemIds?: number[];
  building?: {
    buildYear: string;
    renovatedYear: string;
    totalArea: number;
    heating: string;
    ventilation: string;
    cooling: string;
    standards: string;
    bas: string;
    basUrl: string;
    propertyRegisterDesignation: string;
    installedSolarPanelCapacity: number;
  };
  location?: {
    streetAddress1: string;
    streetAddress2: string;
    place: string;
    zipCode: string;
    county: string;
    country: string;
    location: any;
  };
  assetSolar?: {
    installedCapacity: number;
  };
  assetSolarInverter?: {
    installedCapacity: number;
  };
  assetSolarString?: {
    installedCapacity: number;
    numberOfPanels: number;
  };
  assetStreetLight?: {
    voltage: number;
    manufacturerType: string;
    lightSource: string;
    power: number;
    dimmable: boolean;
  };
  assetStreetLightJunctionBox?: {
    totalTerminals: number;
    voltage: number;
    ignition: string;
  };
  assetVentilation?: {
    placement: string;
    manufacturer: string;
    model: string;
    ratedCapacity: number;
    ventilationZone: string;
    heating: string;
    cooling: string;
    heatExchangerType: number;
    isDemandControlled: boolean;
    isPressureControlled: boolean;
    driverTypes: number;
  };
  assetHeatPump?: {
    manufacturer: string;
    model: string;
  };
  assetDistributionBoard?: {
    placement: string;
    distributionBoardCircuits?: Array<{
      id?: number;
      circuitId: string;
      circuitNumber: number;
      description?: string;
      maxCurrent?: number;
      continousCurrent?: number;
      wireDimentions?: string;
      meteredBy?: string;
      driverTypes?: number;
    }>;
  };
  assetBuildingAutomation?: {
    manufacturer: string;
    driverTypes: number;
  };
  assetBoiler?: {
    manufacturer: string;
    model: string;
  };
  assetHeatExchanger?: {
    placement: string;
    heatExchangerInlet: number;
    heatExchangerOutlet: number;
    heatExchangerType: number;
    inletTemperature: number;
    outletTemperature: number;
    ratedPower: number;
  };
  responsibleIds?: string[];
  energyStandardRequirementId?: number;
  energyPerformanceCertification?: number;
}


export interface AssetListResponse {
  data: ApiAsset[];
  total: number;
  page: number;
  pageSize: number;
  success: boolean;
  message?: string;
}





// Form field configuration for dynamic form generation
export interface FormFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea' | 'coordinate' | 'date';
  required: boolean;
  placeholder?: string;
  options?: { value: string | number; label: string }[];
  min?: number;
  max?: number;
  unit?: string;
}

export const ENGLISH_FORM_CONFIGS: Record<string, FormFieldConfig[]> = {
  building: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Enter building name' },
    { key: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Optional description' },
    { key: 'streetAddress', label: 'Street Address', type: 'text', required: true, placeholder: 'Doneheia 7' },
    { key: 'zipCode', label: 'Zip Code', type: 'text', required: true, placeholder: '4516', min: 4, max: 4 },
    { key: 'place', label: 'City', type: 'text', required: true, placeholder: 'Mandal' },
    { key: 'country', label: 'Country', type: 'text', required: true, placeholder: 'Norway' },
    { key: 'totalArea', label: 'Total Area', type: 'number', required: false, placeholder: '1000', unit: 'm²', min: 0 },
    { key: 'buildYear', label: 'Build Year', type: 'number', required: false, placeholder: '2020', min: 1800, max: new Date().getFullYear() + 5 }
  ],

  solar: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Solar Panel Array 1' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'installedCapacity', label: 'Installed Capacity', type: 'number', required: true, unit: 'kW', min: 0, placeholder: '10.5' }
  ],

  solar_inverter: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Inverter 1' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'installedCapacity', label: 'Capacity', type: 'number', required: true, unit: 'kW', min: 0, placeholder: '5.0' }
  ],

  solar_string: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'String 1' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'installedCapacity', label: 'Installed Capacity', type: 'number', required: true, unit: 'kW', min: 0, placeholder: '5.0' },
    { key: 'numberOfPanels', label: 'Number of Panels', type: 'number', required: true, min: 1, placeholder: '20' }
  ],

  street_light: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Street Light 1' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'voltage', label: 'Voltage', type: 'number', required: true, unit: 'V', min: 0, placeholder: '230' },
    { key: 'manufacturerType', label: 'Manufacturer/Type', type: 'text', required: false, placeholder: 'Philips CityTouch' },
    {
      key: 'lightSource',
      label: 'Light Source',
      type: 'select',
      required: true,
      options: [
        { value: 'LED', label: 'LED' },
        { value: 'HALOGEN', label: 'Halogen' },
        { value: 'SODIUM', label: 'Sodium' },
        { value: 'FLUORESCENT', label: 'Fluorescent' }
      ]
    },
    { key: 'power', label: 'Power', type: 'number', required: true, unit: 'W', min: 0, placeholder: '150' },
    {
      key: 'dimmable',
      label: 'Dimmable',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' }
      ]
    }
  ],

  street_light_junction_box: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Junction Box 1' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'totalTerminals', label: 'Total Terminals', type: 'number', required: true, min: 1, placeholder: '8' },
    { key: 'voltage', label: 'Voltage', type: 'number', required: true, unit: 'V', min: 0, placeholder: '230' },
    { key: 'ignition', label: 'Ignition Type', type: 'text', required: false, placeholder: 'Photoelectric' }
  ],

  ventilation: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Main Ventilation Unit' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'placement', label: 'Location/Placement', type: 'text', required: true, placeholder: 'Roof level' },
    { key: 'manufacturer', label: 'Manufacturer', type: 'text', required: true, placeholder: 'Swegon' },
    { key: 'model', label: 'Model', type: 'text', required: true, placeholder: 'GOLD RX 04' },
    { key: 'ratedCapacity', label: 'Rated Capacity', type: 'number', required: true, unit: 'm³/h', min: 0, placeholder: '1000' },
    { key: 'ventilationZone', label: 'Ventilation Zone', type: 'text', required: false, placeholder: 'Zone A' },
    { key: 'heating', label: 'Heating System', type: 'text', required: false, placeholder: 'Electric heating coil' },
    { key: 'cooling', label: 'Cooling System', type: 'text', required: false, placeholder: 'Chilled water coil' },
    {
      key: 'heatExchangerType',
      label: 'Heat Exchanger Type',
      type: 'select',
      required: false,
      options: [
        { value: '0', label: 'None' },
        { value: '1', label: 'Plate Heat Exchanger' },
        { value: '2', label: 'Rotary Heat Exchanger' },
        { value: '3', label: 'Cross-flow Heat Exchanger' }
      ]
    },
    {
      key: 'isDemandControlled',
      label: 'Demand Controlled',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' }
      ]
    },
    {
      key: 'isPressureControlled',
      label: 'Pressure Controlled',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' }
      ]
    },
    {
      key: 'driverTypes',
      label: 'Driver Types',
      type: 'select',
      required: false,
      options: [
        { value: '1', label: 'Variable Speed Drive' },
        { value: '2', label: 'Fixed Speed' },
        { value: '3', label: 'Multi-Speed' }
      ]
    }
  ],

  heat_pump: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Main Heat Pump' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'manufacturer', label: 'Manufacturer', type: 'text', required: true, placeholder: 'NIBE' },
    { key: 'model', label: 'Model', type: 'text', required: true, placeholder: 'F1245-12' }
  ],

  distribution_board: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Main Distribution Board' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'placement', label: 'Location/Placement', type: 'text', required: true, placeholder: 'In the basement' }
  ],

  building_automation: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Main BAS System' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'manufacturer', label: 'Manufacturer', type: 'text', required: true, placeholder: 'Schneider Electric' },
    {
      key: 'driverTypes',
      label: 'Driver Types',
      type: 'select',
      required: false,
      options: [
        { value: '1', label: 'BACnet' },
        { value: '2', label: 'Modbus' },
        { value: '3', label: 'KNX' },
        { value: '4', label: 'LON' }
      ]
    }
  ],

  boiler: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Main Boiler' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'manufacturer', label: 'Manufacturer', type: 'text', required: true, placeholder: 'Viessmann' },
    { key: 'model', label: 'Model', type: 'text', required: true, placeholder: 'Vitocrossal 300' }
  ],

  heat_exchanger: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Heat Exchanger 1' },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'placement', label: 'Location', type: 'text', required: true, placeholder: 'Mechanical room' },
    { key: 'heatExchangerInlet', label: 'Heat Exchanger Inlet', type: 'number', required: false, unit: '°C', placeholder: '80' },
    { key: 'heatExchangerOutlet', label: 'Heat Exchanger Outlet', type: 'number', required: false, unit: '°C', placeholder: '60' },
    {
      key: 'heatExchangerType',
      label: 'Heat Exchanger Type',
      type: 'select',
      required: true,
      options: [
        { value: '0', label: 'Plate Heat Exchanger' },
        { value: '1', label: 'Shell and Tube' },
        { value: '2', label: 'Coil Heat Exchanger' },
        { value: '3', label: 'Regenerative' }
      ]
    },
    { key: 'inletTemperature', label: 'Inlet Temperature', type: 'number', required: false, unit: '°C', placeholder: '85' },
    { key: 'outletTemperature', label: 'Outlet Temperature', type: 'number', required: false, unit: '°C', placeholder: '65' },
    { key: 'ratedPower', label: 'Rated Power', type: 'number', required: true, unit: 'kW', min: 0, placeholder: '50' }
  ]
};


export const NORWEGIAN_FORM_CONFIGS: Record<string, FormFieldConfig[]> = {
  building: [
    { key: 'name', label: 'Navn', type: 'text', required: true, placeholder: 'Skriv navn på bygget' },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'streetAddress', label: 'Gate adresse', type: 'text', required: true, placeholder: 'Doneheia 7' },
    { key: 'zipCode', label: 'Post kode', type: 'text', required: true, placeholder: '4516', min: 4, max: 4 },
    { key: 'place', label: 'By', type: 'text', required: true, placeholder: 'Mandal' },
    { key: 'country', label: 'Land', type: 'text', required: true, placeholder: 'Norway' },
    { key: 'totalArea', label: 'Total Areal', type: 'number', required: false, placeholder: '1000', unit: 'm²', min: 0 },
    { key: 'buildYear', label: 'Bygningsår', type: 'number', required: false, placeholder: '2020', min: 1800, max: new Date().getFullYear() + 5 }
  ],

  solar: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'installedCapacity', label: 'Kapasitet', type: 'number', required: true, unit: 'kW', min: 0, placeholder: '10.5' }
  ],

  solar_inverter: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'installedCapacity', label: 'Kapasitet', type: 'number', required: true, unit: 'kW', min: 0, placeholder: '5.0' }
  ],

  solar_string: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'installedCapacity', label: 'Installert Kapasitet', type: 'number', required: true, unit: 'kW', min: 0, placeholder: '5.0' },
    { key: 'numberOfPanels', label: 'Antall Paneler', type: 'number', required: true, min: 1, placeholder: '20' }
  ],

  street_light: [
    { key: 'name', label: 'Navn', type: 'text', required: true, placeholder: 'Street Light 1' },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'voltage', label: 'Spenning', type: 'number', required: true, unit: 'V', min: 0, placeholder: '230' },
    { key: 'manufacturerType', label: 'Produsent', type: 'text', required: false, placeholder: 'Philips CityTouch' },
    {
      key: 'lightSource',
      label: 'Lyskilde',
      type: 'select',
      required: true,
      options: [
        { value: 'LED', label: 'LED' },
        { value: 'HALOGEN', label: 'Halogen' },
        { value: 'SODIUM', label: 'Sodium' },
        { value: 'FLUORESCENT', label: 'Fluorescent' }
      ]
    },
    { key: 'power', label: 'Kraft', type: 'number', required: true, unit: 'W', min: 0, placeholder: '150' },
    {
      key: 'dimmable',
      label: 'Dimbar',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'Ja' },
        { value: 'false', label: 'Nei' }
      ]
    }
  ],

  street_light_junction_box: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'totalTerminals', label: 'Antall Terminaler', type: 'number', required: true, min: 1, placeholder: '8' },
    { key: 'voltage', label: 'Spenning', type: 'number', required: true, unit: 'V', min: 0, placeholder: '230' },
    { key: 'ignition', label: 'Tenningstype', type: 'text', required: false }
  ],

  ventilation: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'placement', label: 'Plassering', type: 'text', required: true, placeholder: '2. Etasje' },
    { key: 'manufacturer', label: 'Produsent', type: 'text', required: true, placeholder: 'Swegon' },
    { key: 'model', label: 'Model', type: 'text', required: true, placeholder: 'GOLD RX 04' },
    { key: 'ratedCapacity', label: 'Nominell Kapasitet', type: 'number', required: true, unit: 'm³/h', min: 0, placeholder: '1000' },
    { key: 'ventilationZone', label: 'Ventilasjons Sone', type: 'text', required: false, placeholder: 'Zone A' },
    { key: 'heating', label: 'Varmesystem', type: 'text', required: false, placeholder: 'Elektrisk varmebatteri' },
    { key: 'cooling', label: 'Kjølesystem', type: 'text', required: false, placeholder: 'Kjølt vannbatteri' },
    {
      key: 'heatExchangerType',
      label: 'Varmeveksler Type',
      type: 'select',
      required: false,
      options: [
        { value: '0', label: 'Ingen' },
        { value: '1', label: 'Plate Varmeveksler' },
        { value: '2', label: 'Roterende Varmeveksler' },
        { value: '3', label: 'Kryssstrømsvarmeveksler' }
      ]
    },
    {
      key: 'isDemandControlled',
      label: 'Etterspørselskontrollert',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'Ja' },
        { value: 'false', label: 'Nei' }
      ]
    },
    {
      key: 'isPressureControlled',
      label: 'Trykkkontrollert',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'Ja' },
        { value: 'false', label: 'Nei' }
      ]
    },
    {
      key: 'driverTypes',
      label: 'Drivertyper',
      type: 'select',
      required: false,
      options: [
        { value: '1', label: 'Kjøring med variabel hastighet' },
        { value: '2', label: 'Fast hastighet' },
        { value: '3', label: 'Multi-Speed' }
      ]
    }
  ],

  heat_pump: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'manufacturer', label: 'Produsent', type: 'text', required: true, placeholder: 'NIBE' },
    { key: 'model', label: 'Model', type: 'text', required: true, placeholder: 'F1245-12' }
  ],

  distribution_board: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'placement', label: 'Plassering', type: 'text', required: true, placeholder: 'Kjeller' }
  ],

  building_automation: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'manufacturer', label: 'Produsent', type: 'text', required: true, placeholder: 'Schneider Electric' },
    {
      key: 'driverTypes',
      label: 'Driver Type',
      type: 'select',
      required: false,
      options: [
        { value: '1', label: 'BACnet' },
        { value: '2', label: 'Modbus' },
        { value: '3', label: 'KNX' },
        { value: '4', label: 'LON' }
      ]
    }
  ],

  boiler: [
    { key: 'name', label: 'Kjele', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'manufacturer', label: 'Produsent', type: 'text', required: true, placeholder: 'Viessmann' },
    { key: 'model', label: 'Model', type: 'text', required: true, placeholder: 'Vitocrossal 300' }
  ],

  heat_exchanger: [
    { key: 'name', label: 'Navn', type: 'text', required: true },
    { key: 'description', label: 'Beskrivelse', type: 'textarea', required: false },
    { key: 'placement', label: 'Plassering', type: 'text', required: true, placeholder: 'Mechanical room' },
    { key: 'heatExchangerInlet', label: 'Varmevekslerinnløp', type: 'number', required: false, unit: '°C', placeholder: '80' },
    { key: 'heatExchangerOutlet', label: 'Uttak for varmeveksler', type: 'number', required: false, unit: '°C', placeholder: '60' },
    {
      key: 'heatExchangerType',
      label: 'Varmeveksler Type',
      type: 'select',
      required: true,
      options: [
        { value: '0', label: 'Platevarmeveksler' },
        { value: '1', label: 'Skall og rør' },
        { value: '2', label: 'Batterivarmeveksler' },
        { value: '3', label: 'Regenerativ' }
      ]
    },
    { key: 'inletTemperature', label: 'Innløpstemperatur', type: 'number', required: false, unit: '°C', placeholder: '85' },
    { key: 'outletTemperature', label: 'Utløpstemperatur', type: 'number', required: false, unit: '°C', placeholder: '65' },
    { key: 'ratedPower', label: 'Nominell effekt', type: 'number', required: true, unit: 'kW', min: 0, placeholder: '50' }
  ]
};

// Mapper class to convert  forms to full API requests
export class AssetFormMapper {
  // Map form to full API request
  static mapToApiRequest(
    Form: AssetFormUnion,
    formType: string
  ): ApiCreateAssetRequest {
    // Base request with smart defaults

    const apiRequest: ApiCreateAssetRequest = {
      name: Form.name,
      description: Form.description || '',
      assetTypeId: Form.assetTypeId,
      tag: Form.tag || '',
      parentId: Form.parentId,
      inheritFromType: true,
      assetAvailability: 1,
      heatingDegree: 50,
      temperatureDependentConsumption: 1,
      energyReferenceYear: new Date().getFullYear(),
      fileReferences: [],
      categoryItemIds: [],
      responsibleIds: [],
      energyStandardRequirementId: 0,
      energyPerformanceCertification: 0
    };

    // Add type-specific data
    switch (formType) {
      case 'building':
        const buildingForm = Form as BuildingForm;
        apiRequest.building = {
          buildYear: buildingForm.buildYear ? new Date(buildingForm.buildYear, 0, 1).toISOString() : new Date().toISOString(),
          renovatedYear: buildingForm.renovatedYear ? new Date(buildingForm.renovatedYear, 0, 1).toISOString() : new Date().toISOString(),
          totalArea: buildingForm.totalArea || 0,
          heating: '',
          ventilation: '',
          cooling: '',
          standards: '',
          bas: '',
          basUrl: '',
          propertyRegisterDesignation: '',
          installedSolarPanelCapacity: 0
        };

        apiRequest.location = {
          streetAddress1: buildingForm.streetAddress,
          streetAddress2: '',
          place: buildingForm.place,
          zipCode: buildingForm.zipCode,
          county: '',
          country: buildingForm.country,
          location: buildingForm.coordinates ? {
            type: "Point",
            coordinates: [
              buildingForm.coordinates.longitude,  // longitude first
              buildingForm.coordinates.latitude   // latitude second
            ]
          } : null
        };
        break;

      case 'solar':
        const solarForm = Form as SolarForm;
        apiRequest.assetSolar = {
          installedCapacity: solarForm.installedCapacity
        };
        break;

      case 'solar_inverter':
        const inverterForm = Form as SolarInverterForm;
        apiRequest.assetSolarInverter = {
          installedCapacity: inverterForm.installedCapacity
        };
        break;

      case 'solar_string':
        const stringForm = Form as SolarStringForm;
        apiRequest.assetSolarString = {
          installedCapacity: stringForm.installedCapacity,
          numberOfPanels: stringForm.numberOfPanels
        };
        break;

      case 'distribution_board':
        const boardForm = Form as DistributionBoardForm;
        apiRequest.assetDistributionBoard = {
          placement: boardForm.placement
        };
        break;

      case 'ventilation':
        const ventForm = Form as VentilationForm;
        apiRequest.assetVentilation = {
          placement: ventForm.placement || '',
          manufacturer: ventForm.manufacturer || '',
          model: ventForm.model || '',
          ratedCapacity: ventForm.ratedCapacity || 0,
          ventilationZone: ventForm.ventilationZone || '',
          heating: ventForm.heating || '',
          cooling: ventForm.cooling || '',
          heatExchangerType: ventForm.heatExchangerType ? parseInt(ventForm.heatExchangerType) : 0,
          isDemandControlled: ventForm.isDemandControlled === 'true' || ventForm.isDemandControlled === true,
          isPressureControlled: ventForm.isPressureControlled === 'true' || ventForm.isPressureControlled === true,
          driverTypes: ventForm.driverTypes ? parseInt(ventForm.driverTypes) : 1
        };
        break;

      case 'heat_pump':
        const heatPumpForm = Form as HeatPumpForm;
        apiRequest.assetHeatPump = {
          manufacturer: heatPumpForm.manufacturer || '',
          model: heatPumpForm.model || ''
        };
        break;

      case 'boiler':
        const boilerForm = Form as BoilerForm;
        apiRequest.assetBoiler = {
          manufacturer: boilerForm.manufacturer || '',
          model: boilerForm.model || ''
        };
        break;

      case 'heat_exchanger':
        const heatExForm = Form as HeatExchangerForm;
        // Map string values to numbers for heat exchanger type
        const getHeatExchangerTypeValue = (type: string): number => {
          const typeMap: Record<string, number> = {
            '0': 0, // Plate Heat Exchanger
            '1': 1, // Shell and Tube
            '2': 2, // Coil Heat Exchanger
            '3': 3, // Regenerative
            'PLATE': 0,
            'SHELL_TUBE': 1,
            'COIL': 2,
            'REGENERATIVE': 3
          };
          return typeMap[type] || 0;
        };

        apiRequest.assetHeatExchanger = {
          placement: heatExForm.placement || '',
          heatExchangerInlet: heatExForm.heatExchangerInlet || 0,
          heatExchangerOutlet: heatExForm.heatExchangerOutlet || 0,
          heatExchangerType: heatExForm.heatExchangerType ? getHeatExchangerTypeValue(heatExForm.heatExchangerType) : 0,
          inletTemperature: heatExForm.inletTemperature || 0,
          outletTemperature: heatExForm.outletTemperature || 0,
          ratedPower: heatExForm.ratedPower || 0
        };
        break;

      case 'street_light':
        const streetLightForm = Form as StreetLightForm;
        apiRequest.assetStreetLight = {
          voltage: streetLightForm.voltage || 0,
          manufacturerType: streetLightForm.manufacturerType || '',
          lightSource: streetLightForm.lightSource || '',
          power: streetLightForm.power || 0,
          dimmable: streetLightForm.dimmable === 'true' || streetLightForm.dimmable === true  // Handle both string and boolean
        };
        break;

      case 'street_light_junction_box':
        const junctionForm = Form as StreetLightJunctionBoxForm;
        apiRequest.assetStreetLightJunctionBox = {
          totalTerminals: junctionForm.totalTerminals || 0,
          voltage: junctionForm.voltage || 0,
          ignition: junctionForm.ignition || ''
        };
        break;

      case 'building_automation':
        const baForm = Form as BuildingAutomationForm;
        apiRequest.assetBuildingAutomation = {
          manufacturer: baForm.manufacturer || '',
          driverTypes: baForm.driverTypes ? parseInt(baForm.driverTypes) : 1
        };
        break;
    }

    return apiRequest;
  }

  // Convert API asset back to form for editing
  static mapFromApiAsset(apiAsset: ApiAsset): { form: AssetFormUnion | AssetForm; formType: string } {
    const baseForm = {
      name: apiAsset.name,
      description: apiAsset.description,
      assetTypeId: apiAsset.assetTypeId,
      tag: apiAsset.tag,
      parentId: apiAsset.parentId || undefined
    };

    // Determine form type and create appropriate form
    if (apiAsset.building) {
      return {
        form: {
          ...baseForm,
          streetAddress: apiAsset.location?.streetAddress1 || '',
          place: apiAsset.location?.place || '',
          zipCode: apiAsset.location?.zipCode || '',
          country: apiAsset.location?.country || '',
          totalArea: apiAsset.building.totalArea,
          buildYear: apiAsset.building.buildYear ? new Date(apiAsset.building.buildYear).getFullYear() : undefined,
          renovatedYear: apiAsset.building.renovatedYear ? new Date(apiAsset.building.renovatedYear).getFullYear() : undefined
        } as BuildingForm,
        formType: 'building'
      };
    }

    if (apiAsset.assetSolar) {
      return {
        form: {
          ...baseForm,
          installedCapacity: apiAsset.assetSolar.installedCapacity
        } as SolarForm,
        formType: 'solar'
      };
    }

    if (apiAsset.assetVentilation) {
      return {
        form: {
          ...baseForm,
          placement: apiAsset.assetVentilation.placement,
          manufacturer: apiAsset.assetVentilation.manufacturer,
          model: apiAsset.assetVentilation.model,
          ratedCapacity: apiAsset.assetVentilation.ratedCapacity,
          ventilationZone: apiAsset.assetVentilation.ventilationZone,
          heating: apiAsset.assetVentilation.heating,
          cooling: apiAsset.assetVentilation.cooling,
          heatExchangerType: apiAsset.assetVentilation.heatExchangerType?.toString(),
          isDemandControlled: apiAsset.assetVentilation.isDemandControlled,
          isPressureControlled: apiAsset.assetVentilation.isPressureControlled,
          driverTypes: apiAsset.assetVentilation.driverTypes?.toString()
        } as VentilationForm,
        formType: 'ventilation'
      };
    }

    if (apiAsset.assetDistributionBoard) {
      return {
        form: {
          ...baseForm,
          placement: apiAsset.assetDistributionBoard.placement
        } as DistributionBoardForm,
        formType: 'distribution_board'
      };
    }

    if (apiAsset.assetHeatPump) {
      return {
        form: {
          ...baseForm,
          manufacturer: apiAsset.assetHeatPump.manufacturer,
          model: apiAsset.assetHeatPump.model
        } as HeatPumpForm,
        formType: 'heat_pump'
      };
    }

    if (apiAsset.assetBoiler) {
      return {
        form: {
          ...baseForm,
          manufacturer: apiAsset.assetBoiler.manufacturer,
          model: apiAsset.assetBoiler.model
        } as BoilerForm,
        formType: 'boiler'
      };
    }

    if (apiAsset.assetHeatExchanger) {
      return {
        form: {
          ...baseForm,
          placement: apiAsset.assetHeatExchanger.placement,
          heatExchangerInlet: apiAsset.assetHeatExchanger.heatExchangerInlet,
          heatExchangerOutlet: apiAsset.assetHeatExchanger.heatExchangerOutlet,
          heatExchangerType: apiAsset.assetHeatExchanger.heatExchangerType?.toString(),
          inletTemperature: apiAsset.assetHeatExchanger.inletTemperature,
          outletTemperature: apiAsset.assetHeatExchanger.outletTemperature,
          ratedPower: apiAsset.assetHeatExchanger.ratedPower
        } as HeatExchangerForm,
        formType: 'heat_exchanger'
      };
    }

    if (apiAsset.assetStreetLight) {
      return {
        form: {
          ...baseForm,
          voltage: apiAsset.assetStreetLight.voltage,
          manufacturerType: apiAsset.assetStreetLight.manufacturerType,
          lightSource: apiAsset.assetStreetLight.lightSource,
          power: apiAsset.assetStreetLight.power,
          dimmable: apiAsset.assetStreetLight.dimmable
        } as StreetLightForm,
        formType: 'street_light'
      };
    }

    if (apiAsset.assetStreetLightJunctionBox) {
      return {
        form: {
          ...baseForm,
          totalTerminals: apiAsset.assetStreetLightJunctionBox.totalTerminals,
          voltage: apiAsset.assetStreetLightJunctionBox.voltage,
          ignition: apiAsset.assetStreetLightJunctionBox.ignition
        } as StreetLightJunctionBoxForm,
        formType: 'street_light_junction_box'
      };
    }

    if (apiAsset.assetBuildingAutomation) {
      return {
        form: {
          ...baseForm,
          manufacturer: apiAsset.assetBuildingAutomation.manufacturer,
          driverTypes: apiAsset.assetBuildingAutomation.driverTypes?.toString()
        } as BuildingAutomationForm,
        formType: 'building_automation'
      };
    }

    // Default fallback
    return {
      form: baseForm as AssetForm,
      formType: 'default'
    };
  }

}

// Form validation
export class FormValidator {
  static validateForm(formData: any, config: FormFieldConfig[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    config.forEach(field => {
      const value = formData[field.key];

      if (field.required && (value === undefined || value === null || value === '')) {
        errors.push(`${field.label} is required`);
        return;
      }

      if (value !== undefined && value !== null && value !== '') {
        if (field.type === 'number') {
          if (isNaN(Number(value))) {
            errors.push(`${field.label} must be a valid number`);
          } else {
            const num = Number(value);
            if (field.min !== undefined && num < field.min) {
              errors.push(`${field.label} must be at least ${field.min}`);
            }
            if (field.max !== undefined && num > field.max) {
              errors.push(`${field.label} must be no more than ${field.max}`);
            }
          }
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  }
}

